import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from '../services/firebase';
import { theme } from '../styles/theme';
import Badge from '@mui/material/Badge';
import useMediaQuery from '@mui/material/useMediaQuery';
import PeopleIcon from '@mui/icons-material/People';
import MessageIcon from '@mui/icons-material/Message';
import LogoutIcon from '@mui/icons-material/Logout';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloseIcon from '@mui/icons-material/Close';
import { useSupportNotifications } from '../context/SupportNotificationsContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadTotal } = useSupportNotifications();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [isMobile]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Erro ao sair:', error);
    }
  };

  const menuItems = [
    { path: '/home', icon: <PeopleIcon />, label: 'Clientes' },
    { path: '/messages', icon: <MessageIcon />, label: 'Mensagens' },
    { path: '/plans', icon: <CardMembershipIcon />, label: 'Planos' },
    {
      path: '/support',
      label: 'Suporte',
      icon: (
        <Badge badgeContent={unreadTotal} color="error" overlap="circular" max={99}>
          <SupportAgentIcon />
        </Badge>
      ),
    },
    { path: '/bug-reports', icon: <BugReportIcon />, label: 'Bug Reports' },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <div style={styles.container}>
      {!isMobile && (
        <aside style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <img
              src="/images/logoMeuNegocio-SF.png"
              alt="Logo"
              style={styles.logo}
            />
          </div>

          <nav style={styles.nav}>
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                style={{
                  ...styles.menuItem,
                  ...(location.pathname === item.path ? styles.menuItemActive : {}),
                }}
                title={item.label}
              >
                {item.icon}
              </button>
            ))}
          </nav>

          <div style={styles.sidebarFooter}>
            <button
              style={styles.logoutButton}
              onClick={handleLogout}
              title="Sair"
            >
              <LogoutIcon />
            </button>
          </div>
        </aside>
      )}

      {isMobile && (
        <header style={styles.mobileTopBar}>
          <button
            type="button"
            onClick={() => setMobileDrawerOpen(true)}
            style={styles.mobileMenuTrigger}
            title="Abrir menu"
            aria-label="Abrir menu"
          >
            <img
              src="/images/logoMeuNegocio-SF.png"
              alt="Menu"
              style={styles.mobileLogo}
            />
          </button>
        </header>
      )}

      {isMobile && mobileDrawerOpen && (
        <>
          <div
            style={styles.mobileDrawerOverlay}
            onClick={() => setMobileDrawerOpen(false)}
          />
          <aside style={styles.mobileDrawer}>
            <div style={styles.mobileDrawerHeader}>
              <img
                src="/images/logoMeuNegocio-SF.png"
                alt="Logo"
                style={styles.logo}
              />
              <button
                type="button"
                style={styles.mobileCloseButton}
                onClick={() => setMobileDrawerOpen(false)}
                title="Fechar menu"
                aria-label="Fechar menu"
              >
                <CloseIcon />
              </button>
            </div>

            <nav style={styles.nav}>
              {menuItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  style={{
                    ...styles.menuItem,
                    ...(location.pathname === item.path ? styles.menuItemActive : {}),
                  }}
                  title={item.label}
                >
                  {item.icon}
                </button>
              ))}
            </nav>

            <div style={styles.sidebarFooter}>
              <button
                style={styles.logoutButton}
                onClick={handleLogout}
                title="Sair"
              >
                <LogoutIcon />
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main Content */}
      <main
        style={{
          ...styles.main,
          ...(isMobile ? styles.mainMobile : {}),
        }}
      >
        {children}
      </main>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    minHeight: '100vh',
    background: '#f5f5f5',
    position: 'relative',
  },
  sidebar: {
    width: '80px',
    background: theme.gradient,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
    position: 'fixed',
    height: '100vh',
    zIndex: 1000,
  },
  sidebarHeader: {
    padding: '20px 10px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: '50px',
    height: '50px',
    objectFit: 'contain',
  },
  mobileLogo: {
    width: '40px',
    height: '40px',
    objectFit: 'contain',
  },
  mobileTopBar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '64px',
    background: theme.gradient,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    zIndex: 1000,
  },
  mobileMenuTrigger: {
    border: 'none',
    background: 'transparent',
    padding: '6px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  mobileDrawerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.45)',
    zIndex: 1090,
  },
  mobileDrawer: {
    width: '88px',
    background: theme.gradient,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '2px 0 10px rgba(0, 0, 0, 0.2)',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 1100,
  },
  mobileDrawerHeader: {
    padding: '12px 8px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
  },
  mobileCloseButton: {
    border: 'none',
    background: 'rgba(255, 255, 255, 0.15)',
    color: theme.colors.white,
    borderRadius: '8px',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  nav: {
    flex: 1,
    padding: '20px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'transparent',
    border: 'none',
    color: theme.colors.white,
    fontSize: '28px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    borderLeft: '4px solid transparent',
    width: '100%',
  },
  menuItemActive: {
    background: 'rgba(255, 255, 255, 0.15)',
    borderLeft: `4px solid ${theme.colors.white}`,
  },
    sidebarFooter: {
    padding: '15px 10px',
  },
  logoutButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '15px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: `2px solid ${theme.colors.white}`,
    borderRadius: '8px',
    color: theme.colors.white,
    fontSize: '24px',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  main: {
    marginLeft: '80px',
    flex: 1,
    minHeight: '100vh',
  },
  mainMobile: {
    marginLeft: 0,
    marginTop: '64px',
    minHeight: 'calc(100vh - 64px)',
  },
};

export default Layout;
